import * as THREE from 'three'
import { resolveImportedModelUnitContextFromGltf } from '../imported-model-unit-context'
import { resolveImportedDisplayName } from '../original-node-name'
import type { AssemblyManifest, AssemblyNode, Matrix4Tuple } from '../types'
import { asRecord } from '../utils'
import {
  cloneMetadata,
  resolveNodePmi,
  resolveNodeRecords,
  toMatrixTuple,
} from '../assembly-node-utils'
import {
  cloneGlbHierarchyUserDataMeta,
  collectGlbHierarchy,
  type GlbHierarchyNode,
} from '../glb'

/**
 * 创建 complete manifest 的 occurrence 节点。
 *
 * @param nodeId occurrence 稳定 instanceId。
 * @param definitionNodeId 可由多个 occurrence 共享的 node_id。
 * @param parentNodeId 跳过 internal 后的业务父 occurrence。
 * @param object 当前 occurrence 的运行时 Object3D。
 * @param semanticKind node_type 声明的业务语义。
 * @param transform 相对业务父 occurrence 的矩阵差。
 * @returns 可进入 manifest 的业务节点。
 */
function createCompleteGlbAssemblyNode(
  nodeId: string,
  definitionNodeId: string,
  parentNodeId: string | null,
  object: THREE.Object3D,
  semanticKind: PuppyCAD.CompleteGlbBusinessSemanticKind,
  transform: Matrix4Tuple,
): AssemblyNode {
  return {
    nodeId,
    definitionNodeId,
    parentNodeId,
    name: resolveImportedDisplayName(object, object.name.trim() || object.type),
    semanticKind,
    transform,
    pmi: resolveNodePmi(resolveNodeRecords(object)),
  }
}

/**
 * 按 parser association 建立原始 glTF nodeIndex 到运行时 Object3D 的唯一索引。
 *
 * @param root 当前投影 scene。
 * @param associations GLTFLoader parser association 表。
 * @returns nodeIndex 到 Object3D 的索引。
 */
function createRuntimeObjectByNodeIndex(
  root: THREE.Object3D,
  associations: ReadonlyMap<unknown, unknown>,
): ReadonlyMap<number, THREE.Object3D> {
  const runtimeObjectByNodeIndex = new Map<number, THREE.Object3D>()

  root.traverse((object) => {
    const association = asRecord(associations.get(object))
    const nodeIndex = association?.nodes
    if (typeof nodeIndex !== 'number' || !Number.isInteger(nodeIndex) || nodeIndex < 0) {
      return
    }
    const duplicated = runtimeObjectByNodeIndex.get(nodeIndex)
    if (duplicated && duplicated !== object) {
      throw new Error(
        `完整 GLB occurrence 运行时绑定重复：nodeIndex=${nodeIndex}, objectUuids=${duplicated.uuid},${object.uuid}`,
      )
    }
    runtimeObjectByNodeIndex.set(nodeIndex, object)
  })
  return runtimeObjectByNodeIndex
}

/**
 * 收集一个原始 glTF node 自身 mesh definition 实例化出来的 Mesh。
 *
 * 多 primitive node 会被 GLTFLoader 表达为 Group + 多个无 node association 的 Mesh；遍历在
 * 遇到其它 node association 时停止，从而不会吞入业务或 internal 子 node 的绘制数据。
 *
 * @param root 当前原始 node 对应的运行时对象。
 * @param nodeIndex 当前原始 node 索引。
 * @param associations GLTFLoader parser association 表。
 * @returns 当前 node 独占的原生 Mesh。
 */
function collectRuntimeMeshesForNode(
  root: THREE.Object3D,
  nodeIndex: number,
  associations: ReadonlyMap<unknown, unknown>,
): readonly THREE.Mesh[] {
  const meshes: THREE.Mesh[] = []

  const visit = (object: THREE.Object3D, isRoot: boolean): void => {
    const association = asRecord(associations.get(object))
    const associatedNodeIndex = association?.nodes
    if (!isRoot && typeof associatedNodeIndex === 'number' && associatedNodeIndex !== nodeIndex) {
      return
    }
    if (object instanceof THREE.Mesh) {
      // 派生拓扑显示对象没有 parser association，不能反向算作 GLB 原始 mesh。
      if (association && (association.nodes !== undefined || association.meshes !== undefined)) {
        meshes.push(object)
      }
      return
    }
    object.children.forEach(child => visit(child, false))
  }

  visit(root, true)
  return meshes
}

/**
 * 计算一个业务 occurrence 相对折叠后业务父 occurrence 的矩阵。
 *
 * @param object 当前业务 occurrence 对象。
 * @param parentObject 折叠 internal 后的业务父对象；业务根为 null。
 * @returns 项目 manifest 使用的局部矩阵。
 */
function createProjectedBusinessTransform(
  object: THREE.Object3D,
  parentObject: THREE.Object3D | null,
): Matrix4Tuple {
  if (!parentObject) {
    return toMatrixTuple(new THREE.Matrix4().identity())
  }
  return toMatrixTuple(
    new THREE.Matrix4().copy(parentObject.matrixWorld).invert().multiply(object.matrixWorld),
  )
}

/**
 * 收集当前业务 occurrence 独占技术子树中的虚拟实体节点。
 *
 * 显式业务子 occurrence 由 occurrence 图单独递归，不能把它的实体提升给父 occurrence；
 * 未标记 internal 层本身不进入设计树，但其中的 Body 仍应提升到最近业务祖先。
 *
 * @param source 通用 GLB 层级中的当前节点。
 * @param businessObjectIds 所有显式业务 Object3D UUID。
 * @param parentObjectId 提升后的业务父节点 UUID。
 * @param parentPath 提升后的业务父路径。
 * @param parentDepth 提升后的业务父深度。
 * @returns 已重绑定父级和路径的虚拟实体节点。
 */
function collectOwnedBodyHierarchyNodes(
  source: GlbHierarchyNode,
  businessObjectIds: ReadonlySet<string>,
  parentObjectId: string,
  parentPath: string,
  parentDepth: number,
): GlbHierarchyNode[] {
  const bodies: GlbHierarchyNode[] = []
  source.children.forEach((child) => {
    if (businessObjectIds.has(child.objectId)) {
      return
    }
    if (child.nodeType === 'Body') {
      bodies.push({
        ...child,
        parentObjectId,
        path: `${parentPath}/${child.name}`,
        depth: parentDepth + 1,
        meta: child.meta ? cloneGlbHierarchyUserDataMeta(child.meta) : undefined,
        children: [],
      })
      return
    }
    bodies.push(...collectOwnedBodyHierarchyNodes(
      child,
      businessObjectIds,
      parentObjectId,
      parentPath,
      parentDepth,
    ))
  })
  return bodies
}

/**
 * 从 occurrence 图构建 complete 模式专用设计树。
 *
 * 通用 hierarchy collector 只负责读取实体、材质与显示元数据；业务节点集合、类型和父子关系
 * 必须完全来自 occurrence 图，因而 internal Object3D 永远不会重新泄漏到设计树。
 *
 * @param file 当前 GLB 文件。
 * @param manifest 已建立的 occurrence manifest。
 * @param sourceObjectByNodeId occurrence 身份到 Object3D 的索引。
 * @param ownedMeshesByNodeId occurrence 独占 Mesh 索引。
 * @returns complete 模式权威设计树。
 */
function createCompleteGlbHierarchy(
  file: PuppyCAD.CompleteGlbSceneAnalyzeInput['file'],
  manifest: AssemblyManifest,
  sourceObjectByNodeId: ReadonlyMap<string, THREE.Object3D>,
  ownedMeshesByNodeId: ReadonlyMap<string, readonly THREE.Mesh[]>,
): GlbHierarchyNode {
  const rootObject = sourceObjectByNodeId.get(manifest.rootNodeId)
  if (!rootObject) {
    throw new Error(`完整 GLB hierarchy 缺少根 Object3D：instanceId=${manifest.rootNodeId}`)
  }
  const { hierarchy: rawHierarchy } = collectGlbHierarchy(rootObject, file)
  const rawHierarchyByObjectId = new Map<string, GlbHierarchyNode>()
  const indexRawHierarchy = (node: GlbHierarchyNode): void => {
    rawHierarchyByObjectId.set(node.objectId, node)
    node.children.forEach(indexRawHierarchy)
  }
  indexRawHierarchy(rawHierarchy)

  const businessObjectIds = new Set<string>()
  sourceObjectByNodeId.forEach((object) => businessObjectIds.add(object.uuid))

  const buildNode = (
    instanceId: string,
    parentObjectId: string | null,
    parentPath: string,
    depth: number,
  ): GlbHierarchyNode => {
    const assemblyNode = manifest.nodesById[instanceId]
    const object = sourceObjectByNodeId.get(instanceId)
    if (!assemblyNode || !object) {
      throw new Error(`完整 GLB hierarchy 缺少 occurrence：instanceId=${instanceId}`)
    }
    const rawNode = rawHierarchyByObjectId.get(object.uuid)
      ?? collectGlbHierarchy(object, file).hierarchy
    const path = parentPath ? `${parentPath}/${assemblyNode.name}` : assemblyNode.name
    const childBusinessNodes = (manifest.childIdsByNodeId[instanceId] ?? []).map(childInstanceId => (
      buildNode(childInstanceId, object.uuid, path, depth + 1)
    ))
    const bodyNodes = collectOwnedBodyHierarchyNodes(
      rawNode,
      businessObjectIds,
      object.uuid,
      path,
      depth,
    )
    const directMeshes = ownedMeshesByNodeId.get(instanceId) ?? []
    const hasDirectGeometry = directMeshes.length > 0
    const hasGeometry = hasDirectGeometry
      || childBusinessNodes.some(child => child.meta?.hasGeometry === true)
    const bodyEntries = bodyNodes.flatMap(body => body.meta?.bodyEntries ?? [])
    return {
      objectId: object.uuid,
      parentObjectId,
      path,
      name: assemblyNode.name,
      nodeType: object.type,
      selectable: hasGeometry,
      depth,
      meta: {
        ...rawNode.meta,
        semanticKind: assemblyNode.semanticKind,
        definitionNodeId: assemblyNode.definitionNodeId,
        assemblyNodeId: instanceId,
        hasGeometry,
        hasDirectGeometry,
        bodyCount: bodyEntries.length > 0 ? bodyEntries.length : rawNode.meta?.bodyCount,
        bodyEntries: bodyEntries.length > 0 ? bodyEntries : rawNode.meta?.bodyEntries,
        selectionObjectId: object.uuid,
      },
      children: [...bodyNodes, ...childBusinessNodes],
    }
  }

  return buildNode(manifest.rootNodeId, null, '', 0)
}

/**
 * 创建 complete occurrence manifest，集中声明返回类型供 hierarchy 投影复用。
 *
 * @param input 场景分析输入。
 * @param rootNodeId manifest 根 occurrence 身份。
 * @param nodesById occurrence 节点索引。
 * @param childIdsByNodeId occurrence 子级索引。
 * @returns complete 场景 manifest。
 */
function createCompleteGlbAssemblyManifest(
  input: PuppyCAD.CompleteGlbSceneAnalyzeInput,
  rootNodeId: string,
  nodesById: Record<string, AssemblyNode>,
  childIdsByNodeId: Record<string, string[]>,
): AssemblyManifest {
  const sceneRecord = asRecord(input.gltf.scene.userData) ?? {}
  return {
    assemblyFileId: input.file.fileId,
    assemblyFileUuid: input.file.uuid,
    version: input.version,
    rootNodeId,
    nodesById,
    childIdsByNodeId,
    referencedPartUuids: [],
    externalReferenceFileIdByUuid: {},
    step: cloneMetadata(sceneRecord.step),
    pmi: cloneMetadata(sceneRecord.pmi),
    unitContext: resolveImportedModelUnitContextFromGltf(input.gltf),
  }
}

/**
 * 只读绑定 complete GLB occurrence 图与 GLTFLoader 运行时对象。
 *
 * 本函数不再解释 node_type，也不根据 mesh、children 或 Object3D 类型生成业务节点。准备阶段
 * occurrence 图是业务层级唯一来源；这里仅绑定对象、投影矩阵并收集 occurrence mesh 所有权。
 *
 * @param input 已解析 GLTF、文件上下文与同一原始 JSON 生成的 occurrence 图。
 * @returns complete 专用 manifest、源对象索引和 mesh 所有权。
 */
export function analyzeCompleteGlbScene(
  input: PuppyCAD.CompleteGlbSceneAnalyzeInput,
): PuppyCAD.CompleteGlbSceneAnalysis {
  input.gltf.scene.updateMatrixWorld(true)
  const runtimeObjectByNodeIndex = createRuntimeObjectByNodeIndex(
    input.gltf.scene,
    input.gltf.parser.associations,
  )
  const selectedRootObject = runtimeObjectByNodeIndex.get(input.occurrenceGraph.selection.nodeIndex)
  if (!selectedRootObject) {
    throw new Error(
      `完整 GLB occurrence 无法定位运行时 Object3D：nodeIndex=${input.occurrenceGraph.selection.nodeIndex}`,
    )
  }

  const explicitSelectedRoot = input.occurrenceGraph.occurrenceByNodeIndex.get(
    input.occurrenceGraph.selection.nodeIndex,
  )
  const rootNodeId = input.occurrenceGraph.selectedRootInstanceId
  const nodesById: Record<string, AssemblyNode> = {}
  const childIdsByNodeId: Record<string, string[]> = {}
  const sourceObjectByNodeId = new Map<string, THREE.Object3D>()
  const ownedMeshesByNodeId = new Map<string, THREE.Mesh[]>()

  if (!explicitSelectedRoot) {
    // 未声明 node_type 的选中根仅作为外层项目根的运行时锚点，组合阶段会折叠而不显示第二个根。
    nodesById[rootNodeId] = createCompleteGlbAssemblyNode(
      rootNodeId,
      rootNodeId,
      null,
      selectedRootObject,
      input.expectedRootKind,
      toMatrixTuple(new THREE.Matrix4().identity()),
    )
    childIdsByNodeId[rootNodeId] = []
    sourceObjectByNodeId.set(rootNodeId, selectedRootObject)
    ownedMeshesByNodeId.set(rootNodeId, [])
  }

  for (const descriptor of input.occurrenceGraph.occurrencesByInstanceId.values()) {
    const object = runtimeObjectByNodeIndex.get(descriptor.nodeIndex)
    if (!object) {
      throw new Error(
        `完整 GLB occurrence 无法定位运行时 Object3D：instanceId=${descriptor.instanceId}, nodeId=${descriptor.nodeId}, nodeIndex=${descriptor.nodeIndex}`,
      )
    }

    const parentNodeId = descriptor.parentInstanceId
      ?? (explicitSelectedRoot ? null : rootNodeId)
    const parentObject = descriptor.parentInstanceId
      ? sourceObjectByNodeId.get(descriptor.parentInstanceId) ?? null
      : explicitSelectedRoot
        ? null
        : selectedRootObject
    if (descriptor.parentInstanceId && !parentObject) {
      throw new Error(
        `完整 GLB occurrence 缺少业务父 Object3D：instanceId=${descriptor.instanceId}, parentInstanceId=${descriptor.parentInstanceId}`,
      )
    }

    nodesById[descriptor.instanceId] = createCompleteGlbAssemblyNode(
      descriptor.instanceId,
      descriptor.nodeId,
      parentNodeId,
      object,
      descriptor.semanticKind,
      createProjectedBusinessTransform(object, parentObject),
    )
    childIdsByNodeId[descriptor.instanceId] = []
    sourceObjectByNodeId.set(descriptor.instanceId, object)
    ownedMeshesByNodeId.set(descriptor.instanceId, [])
    if (parentNodeId) {
      childIdsByNodeId[parentNodeId]?.push(descriptor.instanceId)
    }
  }

  for (const [nodeIndex, owner] of input.occurrenceGraph.meshOwnerByNodeIndex) {
    const object = runtimeObjectByNodeIndex.get(nodeIndex)
    if (!object) {
      throw new Error(
        `完整 GLB mesh owner 无法定位运行时 Object3D：nodeIndex=${nodeIndex}, ownerKind=${owner.kind}`,
      )
    }
    const ownerNodeId = owner.kind === 'project-root'
      ? rootNodeId
      : owner.instanceId
    if (!ownerNodeId || !ownedMeshesByNodeId.has(ownerNodeId)) {
      throw new Error(
        `完整 GLB mesh owner 不存在：nodeIndex=${nodeIndex}, ownerKind=${owner.kind}, instanceId=${owner.instanceId ?? 'none'}`,
      )
    }
    ownedMeshesByNodeId.get(ownerNodeId)?.push(
      ...collectRuntimeMeshesForNode(object, nodeIndex, input.gltf.parser.associations),
    )
  }

  const manifest = createCompleteGlbAssemblyManifest(
    input,
    rootNodeId,
    nodesById,
    childIdsByNodeId,
  )
  return {
    manifest,
    hierarchy: createCompleteGlbHierarchy(
      input.file,
      manifest,
      sourceObjectByNodeId,
      ownedMeshesByNodeId,
    ),
    sourceObjectByNodeId,
    ownedMeshesByNodeId,
    warnings: [],
  }
}
